import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  QUESTION_TYPE_ICONS,
  RESPONSE_PERIOD_DAYS,
  SURVEY_KINDS,
  SURVEY_KIND_ICONS,
  SURVEY_QUESTION_TYPES,
  SURVEY_RESPONSE_STATUSES,
} from '../constants';
import { responseStatusTone } from '../helper/surveyResponseHelper';

/**
 * Single place where survey domain values become human copy.
 *
 * Every screen of the module (list, builder, responses) reads its labels,
 * icons and select options from here, so a kind or a question type is never
 * translated twice with different wording. Keys are spelled out literally on
 * purpose: it keeps them statically analysable by the i18n linter.
 */
export function useSurveyLabels() {
  const { t } = useI18n();

  const kindLabels = computed(() => ({
    visit: t('SURVEYS.KINDS.VISIT'),
    proposal: t('SURVEYS.KINDS.PROPOSAL'),
    conversation: t('SURVEYS.KINDS.CONVERSATION'),
    post_sale: t('SURVEYS.KINDS.POST_SALE'),
    support: t('SURVEYS.KINDS.SUPPORT'),
    listing: t('SURVEYS.KINDS.LISTING'),
    csat: t('SURVEYS.KINDS.CSAT'),
    visit_feedback: t('SURVEYS.KINDS.VISIT_FEEDBACK'),
    kyc: t('SURVEYS.KINDS.KYC'),
    custom: t('SURVEYS.KINDS.CUSTOM'),
  }));

  const questionTypeLabels = computed(() => ({
    rating: t('SURVEYS.QUESTION_TYPES.RATING.LABEL'),
    nps: t('SURVEYS.QUESTION_TYPES.NPS.LABEL'),
    text: t('SURVEYS.QUESTION_TYPES.TEXT.LABEL'),
    long_text: t('SURVEYS.QUESTION_TYPES.LONG_TEXT.LABEL'),
    single_choice: t('SURVEYS.QUESTION_TYPES.SINGLE_CHOICE.LABEL'),
    multi_choice: t('SURVEYS.QUESTION_TYPES.MULTI_CHOICE.LABEL'),
    boolean: t('SURVEYS.QUESTION_TYPES.BOOLEAN.LABEL'),
    likert: t('SURVEYS.QUESTION_TYPES.LIKERT.LABEL'),
    date: t('SURVEYS.QUESTION_TYPES.DATE.LABEL'),
    time: t('SURVEYS.QUESTION_TYPES.TIME.LABEL'),
    email: t('SURVEYS.QUESTION_TYPES.EMAIL.LABEL'),
    phone: t('SURVEYS.QUESTION_TYPES.PHONE.LABEL'),
    number: t('SURVEYS.QUESTION_TYPES.NUMBER.LABEL'),
    document_id: t('SURVEYS.QUESTION_TYPES.DOCUMENT_ID.LABEL'),
  }));

  const questionTypeHelpers = computed(() => ({
    rating: t('SURVEYS.QUESTION_TYPES.RATING.HELPER'),
    nps: t('SURVEYS.QUESTION_TYPES.NPS.HELPER'),
    text: t('SURVEYS.QUESTION_TYPES.TEXT.HELPER'),
    long_text: t('SURVEYS.QUESTION_TYPES.LONG_TEXT.HELPER'),
    single_choice: t('SURVEYS.QUESTION_TYPES.SINGLE_CHOICE.HELPER'),
    multi_choice: t('SURVEYS.QUESTION_TYPES.MULTI_CHOICE.HELPER'),
    boolean: t('SURVEYS.QUESTION_TYPES.BOOLEAN.HELPER'),
    likert: t('SURVEYS.QUESTION_TYPES.LIKERT.HELPER'),
    date: t('SURVEYS.QUESTION_TYPES.DATE.HELPER'),
    time: t('SURVEYS.QUESTION_TYPES.TIME.HELPER'),
    email: t('SURVEYS.QUESTION_TYPES.EMAIL.HELPER'),
    phone: t('SURVEYS.QUESTION_TYPES.PHONE.HELPER'),
    number: t('SURVEYS.QUESTION_TYPES.NUMBER.HELPER'),
    document_id: t('SURVEYS.QUESTION_TYPES.DOCUMENT_ID.HELPER'),
  }));

  const statusLabels = computed(() => ({
    pending: t('SURVEYS.RESPONSES.STATUSES.PENDING'),
    sent: t('SURVEYS.RESPONSES.STATUSES.SENT'),
    opened: t('SURVEYS.RESPONSES.STATUSES.OPENED'),
    submitted: t('SURVEYS.RESPONSES.STATUSES.SUBMITTED'),
    expired: t('SURVEYS.RESPONSES.STATUSES.EXPIRED'),
  }));

  const validationMessages = computed(() => ({
    NAME_REQUIRED: t('SURVEYS.VALIDATION.NAME_REQUIRED'),
    KIND_INVALID: t('SURVEYS.VALIDATION.KIND_INVALID'),
    QUESTIONS_MIN: t('SURVEYS.VALIDATION.QUESTIONS_MIN'),
    QUESTIONS_DUPLICATE_KEY: t('SURVEYS.VALIDATION.QUESTIONS_DUPLICATE_KEY'),
    LABEL_REQUIRED: t('SURVEYS.VALIDATION.LABEL_REQUIRED'),
    TYPE_INVALID: t('SURVEYS.VALIDATION.TYPE_INVALID'),
    OPTIONS_MIN: t('SURVEYS.VALIDATION.OPTIONS_MIN'),
    OPTION_LABEL_REQUIRED: t('SURVEYS.VALIDATION.OPTION_LABEL_REQUIRED'),
    SCALE_INVALID: t('SURVEYS.VALIDATION.SCALE_INVALID'),
    MAX_LENGTH_INVALID: t('SURVEYS.VALIDATION.MAX_LENGTH_INVALID'),
    PLACEHOLDER_INVALID: t('SURVEYS.VALIDATION.PLACEHOLDER_INVALID'),
    NUMBER_BOUNDS_INVALID: t('SURVEYS.VALIDATION.NUMBER_BOUNDS_INVALID'),
    VISIBILITY_SOURCE_INVALID: t(
      'SURVEYS.VALIDATION.VISIBILITY_SOURCE_INVALID'
    ),
    VISIBILITY_TYPE_INVALID: t('SURVEYS.VALIDATION.VISIBILITY_TYPE_INVALID'),
    VISIBILITY_VALUE_INVALID: t('SURVEYS.VALIDATION.VISIBILITY_VALUE_INVALID'),
    SUCCESS_TITLE_REQUIRED: t('SURVEYS.VALIDATION.SUCCESS_TITLE_REQUIRED'),
    CTA_URL_REQUIRED: t('SURVEYS.VALIDATION.CTA_URL_REQUIRED'),
    CTA_URL_INVALID: t('SURVEYS.VALIDATION.CTA_URL_INVALID'),
    CTA_LABEL_REQUIRED: t('SURVEYS.VALIDATION.CTA_LABEL_REQUIRED'),
    EXPIRES_INVALID: t('SURVEYS.VALIDATION.EXPIRES_INVALID'),
  }));

  const kindLabel = kind => kindLabels.value[kind] || kindLabels.value.custom;

  const kindIcon = kind => SURVEY_KIND_ICONS[kind] || SURVEY_KIND_ICONS.custom;

  const questionTypeLabel = type =>
    questionTypeLabels.value[type] || questionTypeLabels.value.text;

  const questionTypeHelper = type =>
    questionTypeHelpers.value[type] || questionTypeHelpers.value.text;

  const questionTypeIcon = type =>
    QUESTION_TYPE_ICONS[type] || QUESTION_TYPE_ICONS.text;

  const statusLabel = status =>
    statusLabels.value[status] || statusLabels.value.pending;

  const statusTone = status => responseStatusTone(status);

  const validationMessage = code =>
    code ? validationMessages.value[code] || '' : '';

  const periodLabel = days =>
    Number(days) > 0
      ? t('SURVEYS.RESPONSES.FILTERS.LAST_DAYS', { count: Number(days) })
      : t('SURVEYS.RESPONSES.FILTERS.ALL_TIME');

  const kindOptions = computed(() =>
    SURVEY_KINDS.map(kind => ({ value: kind, label: kindLabel(kind) }))
  );

  const kindFilterOptions = computed(() => [
    { value: '', label: t('SURVEYS.LIST.FILTERS.ALL_KINDS') },
    ...kindOptions.value,
  ]);

  const activeFilterOptions = computed(() => [
    { value: '', label: t('SURVEYS.LIST.FILTERS.ALL_STATUSES') },
    { value: 'active', label: t('SURVEYS.LIST.FILTERS.ONLY_ACTIVE') },
    { value: 'inactive', label: t('SURVEYS.LIST.FILTERS.ONLY_INACTIVE') },
  ]);

  const questionTypeOptions = computed(() =>
    SURVEY_QUESTION_TYPES.map(type => ({
      value: type,
      label: questionTypeLabel(type),
      helper: questionTypeHelper(type),
      icon: questionTypeIcon(type),
    }))
  );

  const responseStatusOptions = computed(() => [
    { value: '', label: t('SURVEYS.RESPONSES.FILTERS.ALL_STATUSES') },
    ...SURVEY_RESPONSE_STATUSES.map(status => ({
      value: status,
      label: statusLabel(status),
    })),
  ]);

  const periodOptions = computed(() =>
    RESPONSE_PERIOD_DAYS.map(days => ({
      value: days,
      label: periodLabel(days),
    }))
  );

  return {
    t,
    kindLabel,
    kindIcon,
    kindOptions,
    kindFilterOptions,
    activeFilterOptions,
    questionTypeLabel,
    questionTypeHelper,
    questionTypeIcon,
    questionTypeOptions,
    statusLabel,
    statusTone,
    responseStatusOptions,
    periodLabel,
    periodOptions,
    validationMessage,
  };
}

export default useSurveyLabels;
